/**
 * <event-console-orders> — the console's orders destination: a scrolling list
 * of <order-card>, one per order, windowed through virtualize() because an
 * event's order list is unbounded and every row is a whole card carrying an
 * animated art overlay, so building one per order is a cost that grows with
 * the event rather than with the screen. Bounced emails first and
 * newest-first within each group (consoleViewModel.orders is pre-sorted —
 * see console.state.ts for why the bounce outranks recency). A search box
 * above the list narrows that same list by buyer name, email, or the ticket
 * number the buyer holds — that last leg only for a session admitted to the
 * event's ticket rows, which is also what picks the label the box states
 * (order-detail.engine.ts's own `filterOrdersByQuery` — see its header for
 * why this destination stays with a plain substring match rather than the
 * door's own fuzzy/German-fold lookup). A routed component in the console's
 * own nested `event-console` area (see dashboard.view.ts's header for why
 * that, not a template branch, is what keeps the destination out of a
 * resettable local field).
 *
 * Refunding every paid order in one batch opens from this destination's own
 * search line rather than a row of its own, because the orders that batch acts
 * on are exactly the list below it. It is absent, not disabled, for a member
 * without `tickets.orders.refund` and for an event holding no paid order at
 * all — and present, rather than absent, for a member who holds the permission
 * but cannot list the event's tickets, since that surface's job is then to say
 * so rather than to leave a control silently missing.
 *
 * A guest order is an order — origin: 'guest' rather than 'sale', minted by
 * the exact same settleOrder path — so this destination also carries the
 * Guests tab's own "Add guest" trigger, riding the search line's trailing end
 * the same way refund-all does: it opens the same <guest-issue-add> dialog and
 * pushes the same guestIssueAction$ the Guests tab pushes (guest-issue.state.ts),
 * never a second issuance path. That line survives an event holding no order at
 * all — the first guest ticket of an event is issued from here, so an empty
 * list may not take the toolbar with it; only the search box goes, having
 * nothing to narrow, and the row itself goes only when it would hold nothing.
 * <guest-issue-sync> (web) is mounted once for the whole console route
 * regardless of which destination is routed, so the
 * write channel is already live here exactly as it is on Guests. The event id
 * and ticket-type catalog it needs are selectedSaleEventId/saleTicketTypes
 * (admin/sale/sale.state.ts) — already folded in by event-console.view.ts's
 * own connect-time fold for the console's whole lifetime, not just while
 * Guests is the routed destination. The card the newly-issued order settles
 * into is the same <order-card> this list already renders — order-card.view.ts
 * carries the guest-vs-sale visual distinction, not this file.
 *
 * Erasing every test order of the event rides that same search line for the
 * same reason refund-all does, and is absent — never disabled — for a member
 * without `tickets.orders.purge` and for an event holding no sandbox order.
 * The confirm names the exact number that disappears; the ids it counted are
 * the ids sent, and `web`'s order-ops.sync.ts drives the existing
 * single-order callable once per id.
 *
 * Every branch below establishes its own scroll region, the way each sibling
 * destination does. The area mounting a destination stretches it to exactly
 * the area's own box and pins its minimum height to zero, so a list taller
 * than that box has no scroll of its own to catch the excess — the console's
 * header stays pinned because it is the area's sibling, and the body scrolls
 * because this view, not the area, is what bounds it.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm, show } from "@mhmo91/schmancy/overlay";
import { tooltip } from "@mhmo91/schmancy/primitives";
import { virtualize } from "@mhmo91/schmancy/virtualizer";
import { css, html, type TemplateResult } from "lit";
import { state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { filter, from, take, takeUntil, tap } from "rxjs";
import type { GuestAddDetail } from "../../admin/sale/guest-issue-add.view.ts";
import "../../admin/sale/guest-issue-add.view.ts";
import {
	guestIssueAction$,
	guestIssueLastGuest,
	guestIssueOutcome,
	guestIssueRefusalReason,
	guestIssueReset$,
	guestIssueResult$,
} from "../../admin/sale/guest-issue.state.ts";
import {
	canIssueGuestTickets,
	canViewSaleReports,
	saleEventOptions,
	saleTicketTypes,
	selectedSaleEventId,
} from "../../admin/sale/sale.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationId } from "../../workspace/organization.state.ts";
import {
	consoleOrdersStatus,
	consoleRowsAnswered,
	consoleTicketsListening,
	consoleViewModel,
} from "./console.state.ts";
import type { OrderFacet } from "./order-detail.engine.ts";
import {
	canPurgeOrders,
	canRefundOrders,
	eventPurgeDraft,
	eventRefundDraft,
	orderActionSettled$,
	orderPurgeAllRequested$,
} from "./order-ops.state.ts";
import {
	consoleOrderFacetCounts,
	consoleOrdersDateRange,
	consoleOrdersFacet,
	consoleOrdersPage,
	consoleOrdersQuery,
} from "./orders.state.ts";
import "./order-card.view.ts";
import "./refunds/refund-all-orders.view.ts";

/** One row's stride down the list, to the pixel rather than as a guess: the
 *  windowed slice lands at an offset summed from this, so an estimate that is
 *  off drifts further the deeper the list is scrolled.
 *
 *  An <order-card> is three lines plus its own inset: `p-4` (32px) + one
 *  `title/md` buyer line (24px) + the `body/sm` email line (16px) + the
 *  `label/sm` count-and-money line (16px) + the two 8px `gap-2` steps between
 *  them = 104px. Every one of those lines is a single line and stays one: the
 *  email truncates, the status label rides beside the buyer name on that same
 *  line rather than under it, and the meta line is one string — so 104px is a
 *  constant rather than the common case.
 *
 *  The trailing 8px is the list's own `gap-2`, folded in: the gap stays on the
 *  flow parent, where the directive cancels only the one gap adjacent to its
 *  own leading spacer, and every earlier gap is offset the spacer must still
 *  account for — so the stride this estimate reports is card + gap. */
const ORDER_ROW_PX = 104 + 8;

@schmancyElement("event-console-orders")
export default class EventConsoleOrders extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: flex;
				flex-direction: column;
			}
		`,
	];

	/** The three filter cells outlive this element, so leaving the destination
	 *  clears them — a narrowing carried back in would state a filtered list as
	 *  the event's whole order set. */
	@state() private writeError = "";
	@state() private purging = false;
	@state() private purgeNotice = "";
	private purgeRequestId: string | null = null;
	override connectedCallback() {
		super.connectedCallback();
		orderActionSettled$
			.pipe(takeUntil(this.disconnecting))
			.subscribe((answer) => {
				if (answer.requestId !== this.purgeRequestId) return;
				this.purging = false;
				this.purgeNotice =
					answer.message ??
					this.t("admin.events.console.purgeAllOrders.toast", {
						count: this.purgeCount,
					});
			});
	}
	private purgeCount = 0;

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		consoleOrdersQuery.set("");
		consoleOrdersFacet.set("");
		consoleOrdersDateRange.replace({ start: null, end: null });
	}

	// Bubbles from either facet-chips instance — the inline hidden md:flex
	// group and the mobile Filters popover's own wrap-mode copy — so a
	// selection made in either always lands on the one piece of state both
	// read.
	private readonly onFacetChange = (
		e: CustomEvent<{ value: string | string[] }>,
	): void => {
		const value = Array.isArray(e.detail.value)
			? (e.detail.value[0] ?? "")
			: e.detail.value;
		consoleOrdersFacet.set(
			value === "guest" || value === "sale" || value === "refunded"
				? value
				: "",
		);
	};

	// Mirrors guest-issue.view.ts's own openAddGuest exactly — same dialog,
	// same write channel, same requestId shape — so a guest ticket issued from
	// here can never diverge from one issued off the Guests tab.
	private readonly openAddGuest = (): void => {
		const eventId = selectedSaleEventId.value;
		if (!eventId) return;
		// The event rides along because the dialog's day chips and its
		// whole-event/day grouping resolve from it: without one it falls back to
		// the flat single-day list, which is a different form of the same dialog.
		const event = saleEventOptions.get().find((o) => o.id === eventId) ?? null;
		// Read as the dialog opens, never at submit: a switch made while it
		// stands open must not move the guest into another organization.
		const orgId = organizationId.value;
		guestIssueReset$.next();
		this.writeError = "";
		show(
			() => html`<guest-issue-add
                .ticketTypes=${saleTicketTypes.value}
                .event=${event}
                .busy=${guestIssueOutcome.value === "pending"}
                .settled=${guestIssueOutcome.value === "settled"}
                .failure=${this.writeError || (guestIssueOutcome.value === "refused" ? this.t(guestIssueRefusalReason.value === "soldOut" ? "admin.sale.guestIssue.refusedBodySoldOut" : "admin.sale.guestIssue.refusedBodyGeneric") : "")}
                @issue=${(e: CustomEvent<GuestAddDetail>) => {
									const detail = e.detail;
									const requestId = crypto.randomUUID();
									this.writeError = "";
									guestIssueLastGuest.set({
										name: detail.name,
										email: detail.email,
									});
									guestIssueResult$
										.pipe(
											filter((result) => result.requestId === requestId),
											take(1),
											takeUntil(this.disconnecting),
										)
										.subscribe((result) => {
											if (!result.ok) this.writeError = result.error;
										});
									guestIssueAction$.next({
										requestId,
										orgId,
										eventId,
										buyer: {
											email: detail.email,
											name: detail.name,
											locale: detail.locale,
										},
										lines: detail.lines,
									});
								}}
            ></guest-issue-add>`,
			{ ariaLabel: this.t("admin.sale.guestIssue.addDialogAriaLabel") },
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	};

	// Two beats, never one, and the second names the exact number of orders
	// that disappear — the same shape order-detail.view.ts's single-order purge
	// takes, because this is that gesture repeated, not a different act. The
	// ids the confirm counted are the ids pushed, so an order that lands in the
	// list between the confirm and the send is not swept up by a count the
	// operator never saw.
	private readonly purgeAllTestOrders = (): void => {
		const draft = eventPurgeDraft.get();
		if (!draft || draft.orderIds.length === 0 || this.purging) return;
		this.purging = true;
		this.purgeNotice = "";
		this.purgeCount = draft.orderIds.length;
		this.purgeRequestId = crypto.randomUUID();
		from(
			confirm({
				title: this.t("admin.events.console.purgeAllOrders.confirmTitle", {
					count: draft.orderIds.length,
				}),
				message: this.t("admin.events.console.purgeAllOrders.confirmBody", {
					count: draft.orderIds.length,
				}),
				confirmText: this.t("admin.events.console.purgeAllOrders.confirmCta", {
					count: draft.orderIds.length,
				}),
				cancelText: this.t("admin.events.console.purgeAllOrders.cancel"),
				variant: "danger",
			}),
		)
			.pipe(
				tap((ok) => {
					if (!ok) {
						this.purging = false;
						return;
					}
					if (!this.purgeRequestId) return;
					orderPurgeAllRequested$.next({
						requestId: this.purgeRequestId,
						eventId: draft.eventId,
						orderIds: draft.orderIds,
					});
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	};

	override render(): TemplateResult {
		const vm = consoleViewModel.get();

		if (!vm) {
			return html`
				<div role="status" aria-busy="true" ${overflowWithin()} class="flex h-full flex-col gap-3 p-4 md:p-6">
					<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
					<schmancy-skeleton class="h-32 w-full rounded-2xl"></schmancy-skeleton>
				</div>
			`;
		}

		if (!canViewSaleReports.get()) {
			return html`
				<div ${overflowWithin()} class="flex h-full flex-col items-start gap-2 px-4 py-8 md:px-6">
					<schmancy-icon class="text-3xl text-surface-onVariant">lock</schmancy-icon>
					<schmancy-typography type="body" token="sm" class="max-w-sm text-surface-onVariant">
						${this.t("admin.events.console.noAccessBody")}
					</schmancy-typography>
				</div>
			`;
		}

		if (consoleOrdersStatus.value === "error") {
			return html`
				<div role="status" ${overflowWithin()} class="flex h-full flex-col items-start gap-2 px-4 py-8 md:px-6">
					<schmancy-typography type="body" token="sm" class="max-w-sm text-error-default">
						${this.t("admin.events.console.ordersLoadFailed")}
					</schmancy-typography>
				</div>
			`;
		}

		if (!consoleRowsAnswered(consoleOrdersStatus.value)) {
			return html`
				<div role="status" aria-busy="true" ${overflowWithin()} class="flex h-full flex-col gap-3 p-4 md:p-6">
					<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
					<schmancy-skeleton class="h-32 w-full rounded-2xl"></schmancy-skeleton>
				</div>
			`;
		}

		const filteredOrders = consoleOrdersPage.get();
		const counts = consoleOrderFacetCounts.get();
		const facetOptions: ReadonlyArray<{
			value: OrderFacet;
			label: string;
			count: number;
			toneClass: string;
			selectedToneStyle: string;
		}> = [
			{
				value: "guest",
				label: this.t("admin.events.console.facetGuestLabel"),
				count: counts.guest,
				toneClass: "",
				selectedToneStyle: "",
			},
			{
				value: "sale",
				label: this.t("admin.events.console.facetSaleLabel"),
				count: counts.sale,
				toneClass: "",
				selectedToneStyle: "",
			},
			{
				// Refunded picks up the same warning-tone ink order-card.view.ts
				// and order-detail.view.ts already spend on a refunded status —
				// the one color this destination treats as "look again." Set both
				// ends of the chip's own selected/unselected split: the Tailwind
				// class colors the ink while unselected, and
				// --filter-chip-selected-color (the component's documented theming
				// hook) carries it through the selected state too — the chip's own
				// inline selectedStyle otherwise overrides any plain class the
				// instant it's selected, which is exactly the moment this tone
				// matters most.
				value: "refunded",
				label: this.t("admin.events.console.facetRefundedLabel"),
				count: counts.refunded,
				toneClass: "text-warning-default",
				selectedToneStyle:
					"--filter-chip-selected-color: var(--schmancy-sys-color-warning-default);",
			},
		];
		const facetChipsTemplate = html`
			${repeat(
				facetOptions,
				(o) => o.value,
				(o) => html`
					<schmancy-filter-chip
						.value=${o.value}
						class=${o.toneClass}
						style=${o.selectedToneStyle}
					>
						${o.label} ${o.count}
					</schmancy-filter-chip>
				`,
			)}
		`;
		const filtersActiveCount = consoleOrdersFacet.value === "" ? 0 : 1;
		const filtersButtonLabel =
			filtersActiveCount > 0
				? this.t("admin.events.console.filtersButtonAriaActive", {
						count: filtersActiveCount,
					})
				: this.t("admin.events.console.filtersButtonAriaInactive");
		const canRefundAll =
			canRefundOrders.get() && eventRefundDraft.get() !== null;
		const canPurgeAll =
			canPurgeOrders.get() && (eventPurgeDraft.get()?.orderIds.length ?? 0) > 0;
		const toolbarHasContent =
			vm.orders.length > 0 ||
			canIssueGuestTickets.get() ||
			canRefundAll ||
			canPurgeAll;

		return html`
			<div ${overflowWithin()} class="flex h-full flex-col gap-4 p-4 md:p-6">
                <schmancy-typography role="status" type="body" token="sm" class="text-error-default">${this.purgeNotice}</schmancy-typography>
				${when(
					toolbarHasContent,
					() => html`
				<div class="flex flex-wrap items-center justify-end gap-2 md:flex-nowrap">
					${when(
						vm.orders.length > 0,
						() => html`
							<schmancy-date-range
								collapse
								type="date"
								.start=${consoleOrdersDateRange.value.start}
								.end=${consoleOrdersDateRange.value.end}
								placeholder=${this.t("admin.events.console.dateRangeLabel")}
								@change=${(
									e: CustomEvent<{
										start: string | null;
										end: string | null;
										preset: string | null;
									}>,
								) => {
									consoleOrdersDateRange.replace({
										start: e.detail.start,
										end: e.detail.end,
									});
								}}
							></schmancy-date-range>
							<schmancy-chips
								class="hidden md:flex flex-1 basis-56"
								.value=${consoleOrdersFacet.value}
								@change=${this.onFacetChange}
							>${facetChipsTemplate}</schmancy-chips>
							<schmancy-icon-button
								class="flex md:hidden"
								icon="filter_list"
								variant="outlined"
								color="neutral"
								aria-label=${filtersButtonLabel}
								${tooltip(filtersButtonLabel)}
								@click=${() =>
									show(
										() => html`
											<div class="flex flex-col gap-3 p-4 w-64 max-w-full">
												<schmancy-chips
													wrap
													.value=${consoleOrdersFacet.value}
													@change=${this.onFacetChange}
												>${facetChipsTemplate}</schmancy-chips>
											</div>
										`,
										{
											ariaLabel: this.t(
												"admin.events.console.filtersButtonLabel",
											),
										},
									)
										.pipe(takeUntil(this.disconnecting))
										.subscribe()}
							></schmancy-icon-button>
							<schmancy-input
								class="order-first min-w-0 basis-full md:order-none md:basis-auto md:flex-1"
								type="search"
								placeholder=${this.t(
									consoleTicketsListening.get()
										? "admin.events.console.searchLabel"
										: "admin.events.console.searchBuyerLabel",
								)}
								aria-label=${this.t(
									consoleTicketsListening.get()
										? "admin.events.console.searchAriaLabel"
										: "admin.events.console.searchBuyerAriaLabel",
								)}
								.value=${consoleOrdersQuery.value}
								@input=${(e: CustomEvent<{ value: string }>) => {
									consoleOrdersQuery.set(e.detail.value);
								}}
							></schmancy-input>
						`,
					)}
					${when(canIssueGuestTickets.get(), () => {
						const hasTypes = saleTicketTypes.value.length > 0;
						const label = hasTypes
							? this.t("admin.sale.guestIssue.addGuestButton")
							: this.t("admin.sale.guestIssue.noTypesBody");
						return html`
								<schmancy-button
									variant="filled"
									class="shrink-0"
									aria-label=${label}
									${tooltip(label)}
									?disabled=${!hasTypes}
									@click=${this.openAddGuest}
								>
									<schmancy-icon slot="prefix" aria-hidden="true">redeem</schmancy-icon>
									<span class="hidden md:inline">${this.t("admin.sale.guestIssue.addGuestButton")}</span>
								</schmancy-button>
							`;
					})}
					${when(canRefundAll, () => {
						const label = this.t(
							"admin.events.console.refundAllOrders.openAria",
						);
						return html`
							<schmancy-icon-button
								icon="currency_exchange"
								variant="outlined"
								color="neutral"
								aria-label=${label}
								${tooltip(label)}
								@click=${() =>
									show(() => html`<refund-all-orders></refund-all-orders>`, {
										ariaLabel: this.t(
											"admin.events.console.refundAllOrders.dialogAriaLabel",
										),
									})
										.pipe(takeUntil(this.disconnecting))
										.subscribe()}
							></schmancy-icon-button>
						`;
					})}
					${when(canPurgeAll, () => {
						const label = this.t(
							"admin.events.console.purgeAllOrders.openAria",
						);
						return html`
								<schmancy-icon-button
									icon="delete_forever"
									variant="outlined"
									color="neutral"
									aria-label=${label}
									${tooltip(label)}
									.busy=${this.purging}
                                    @click=${this.purgeAllTestOrders}
								></schmancy-icon-button>
							`;
					})}
				</div>
					`,
				)}
				${choose(guestIssueOutcome.value, [
					[
						"pending",
						() => html`
						<div class="flex items-center gap-3 rounded-2xl bg-surface-container p-4" role="status" aria-live="polite">
							<schmancy-spinner size="md"></schmancy-spinner>
							<schmancy-typography type="body" token="md">
								${this.t("admin.sale.guestIssue.pendingLabel", { name: guestIssueLastGuest.value.name })}
							</schmancy-typography>
						</div>
					`,
					],
					[
						"settled",
						() => html`
						<div class="flex items-center justify-between gap-3 rounded-2xl bg-surface-container p-4">
							<div class="flex items-center gap-3">
								<schmancy-icon class="text-2xl text-primary-default">check_circle</schmancy-icon>
								<schmancy-typography type="body" token="md">
									${this.t("admin.sale.guestIssue.settledBody", {
										name: guestIssueLastGuest.value.name,
										email: guestIssueLastGuest.value.email,
									})}
								</schmancy-typography>
							</div>
							<schmancy-icon-button
								icon="close"
								aria-label=${this.t("admin.sale.guestIssue.issueAnotherButton")}
								@click=${() => guestIssueReset$.next()}
							></schmancy-icon-button>
						</div>
					`,
					],
					[
						"refused",
						() => html`
						<div class="flex items-center justify-between gap-3 rounded-2xl bg-error-container p-4">
							<div class="flex items-center gap-3">
								<schmancy-icon class="text-2xl text-error-default">error</schmancy-icon>
								<schmancy-typography type="body" token="md">
									${
										guestIssueRefusalReason.value === "soldOut"
											? this.t("admin.sale.guestIssue.refusedBodySoldOut")
											: this.t("admin.sale.guestIssue.refusedBodyGeneric")
									}
								</schmancy-typography>
							</div>
							<schmancy-icon-button
								icon="close"
								aria-label=${this.t("admin.sale.guestIssue.tryAgainButton")}
								@click=${() => guestIssueReset$.next()}
							></schmancy-icon-button>
						</div>
					`,
					],
				])}
				${when(
					vm.orders.length === 0,
					() => html`
						<div class="flex flex-col items-start gap-3 py-8">
							<schmancy-icon class="text-3xl text-surface-onVariant">receipt_long</schmancy-icon>
							<schmancy-typography type="title" token="sm">${this.t("admin.events.console.noOrdersYet")}</schmancy-typography>
						</div>
					`,
					() =>
						when(
							filteredOrders.length === 0,
							() => html`
								<div class="flex flex-col items-start gap-3 py-8">
									<schmancy-icon class="text-3xl text-surface-onVariant">search_off</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.events.console.noMatchTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="max-w-xs text-surface-onVariant">
										${this.t("admin.events.console.noMatchBody")}
									</schmancy-typography>
								</div>
							`,
							() => html`
								<div class="flex flex-col gap-2">
									${virtualize(
										filteredOrders,
										(o) => o.id,
										(o) => html`<order-card .order=${o}></order-card>`,
										{ estimateSize: () => ORDER_ROW_PX },
									)}
								</div>
							`,
						),
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-console-orders": EventConsoleOrders;
	}
}
